import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import {
  OASISFormState,
  OASISSchema,
  OASISFormData,
  FormValue,
  FormSchemaVersion,
} from '../../components/oasis/types';

interface OASISFormActions {
  setSchema: (schema: OASISSchema | null) => void;
  setFormData: (formData: OASISFormData) => void;
  updateFormField: (key: string, value: FormValue) => void;
  setCurrentCategory: (category: string | null) => void;
  setIsLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setIsDirty: (dirty: boolean) => void;
  setLastSaved: (timestamp: Date | null) => void;
  setSchemaVersion: (version: FormSchemaVersion | null) => void;
  resetForm: () => void;
  initializeForm: (visitId: string, initialData?: OASISFormData) => void;
}

export type OASISFormStore = OASISFormState & OASISFormActions;

const initialState: OASISFormState = {
  schema: null,
  formData: {},
  answers: [],
  currentCategory: null,
  isLoading: false,
  error: null,
  isDirty: false,
  lastSaved: null,
  schemaVersion: null,
};

export const useOASISFormStore = create<OASISFormStore>()(
  devtools(
    persist(
      set => ({
        ...initialState,

        setSchema: schema => set({ schema }, false, 'setSchema'),

        setFormData: formData =>
          set({ formData, isDirty: true }, false, 'setFormData'),

        updateFormField: (key, value) =>
          set(
            state => ({
              formData: { ...state.formData, [key]: value },
              isDirty: true,
            }),
            false,
            'updateFormField'
          ),

        setCurrentCategory: currentCategory =>
          set({ currentCategory }, false, 'setCurrentCategory'),

        setIsLoading: isLoading => set({ isLoading }, false, 'setIsLoading'),

        setError: error => set({ error }, false, 'setError'),

        setIsDirty: isDirty => set({ isDirty }, false, 'setIsDirty'),

        setLastSaved: lastSaved =>
          set({ lastSaved, isDirty: false }, false, 'setLastSaved'),

        setSchemaVersion: schemaVersion =>
          set({ schemaVersion }, false, 'setSchemaVersion'),

        resetForm: () => set({ ...initialState }, false, 'resetForm'),

        initializeForm: (_visitId, initialData = {}) =>
          set(
            {
              formData: initialData,
              isDirty: false,
              error: null,
              lastSaved: null,
            },
            false,
            'initializeForm'
          ),
      }),
      {
        name: 'oasis-form-storage',
        // Persist navigation only — visit answers come from the server on load.
        // Persisting formData globally (not keyed by visit) caused empty/stale
        // localStorage to overwrite hydrated visit_data in the form UI.
        partialize: state => ({
          currentCategory: state.currentCategory,
        }),
      }
    ),
    {
      name: 'OASISFormStore',
    }
  )
);
